import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AlertController, IonNav } from '@ionic/angular';
import { CaseService } from 'src/app/services/case.service';
import { CommonService } from 'src/app/services/common.service';
import { PaymentGatewaysPage } from '../payment-plan/payment-gateways/payment-gateways.page';
import { TranslateService } from '@ngx-translate/core';
import { CustomFormPage } from '../custom-form/custom-form.page';

@Component({
  selector: 'app-amount-payment-plan',
  templateUrl: './amount-payment-plan.page.html',
  styleUrls: ['./amount-payment-plan.page.scss'],
})
export class AmountPaymentPlanPage implements OnInit {
  @Input() debtorDetails;
  @Input() caseData;
  @Input() planType;
  paymentPlanForm: FormGroup;
  amountLabel =
    'What is the maximum amount you can afford each salary cycle without jeopardising your priority bills?';
  frequencyLabel = 'What is your salary/income frequency?';
  firstPaymentLabel =
    'What date would you like your first payment to be taken?';
  today: string;
  paymentFrequencyOptions = [
  ];
  paymentFrequencyLabel = {
    3: 'week',
    4: 'month',
    6: this.caseService.isClient('oriel') ? 'fortnight' : 'biweek',
  };
  maxTerms = 0;
  durationForFirstPayment = 0;
  totalAmount = 0.0;
  caseList = [];
  adjustFinalPaymentMinimum = false;
  paymentAmountLimitMin = null;
  client_id: string;
  constructor(
    private formBuilder: FormBuilder,
    private commonService: CommonService,
    private caseService: CaseService,
    private ionNav: IonNav,
    private alertCtrl: AlertController,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.today = new Date().toISOString().split('T')[0];
    this.initForm();
  }

  ionViewWillEnter() {
    if (localStorage.getItem('portal_settings')) {
      let settings = JSON.parse(localStorage.getItem('portal_settings'));

      if (settings.arrangement && settings.arrangement.amount_first) {
        this.amountLabel =
          settings.arrangement.amount_first.payment_amount_label;
        this.frequencyLabel = settings.arrangement.amount_first.freq_label;
        this.firstPaymentLabel =
          settings.arrangement.amount_first.first_payment_label;

        if (settings.arrangement.amount_first.max_duration_first_payment) {
          this.durationForFirstPayment = parseInt(
            settings.arrangement.amount_first.max_duration_first_payment
          );
          this.firstPaymentLabel +=
            ' (Maximum ' + this.durationForFirstPayment + ' Days)';
        }

        this.client_id = this.caseData?.client_id?.toString();

        let amountFirst = settings.arrangement.amount_first;

        let clientOverride = amountFirst.client_override || [];
        let maxPlanLimits = amountFirst.max_payment_plan_limit || [];

        const index = clientOverride.findIndex(
          id => id.trim() === this.client_id
        );

        if (index !== -1 && maxPlanLimits[index]) {
          // Client matched → use corresponding max limit
          this.maxTerms = parseInt(maxPlanLimits[index]);
        } else {
          // Default fallback
          this.maxTerms = parseInt(amountFirst.max_payment_term);
        }

        // Filter payment frequency options based on available freq_options
        const availableFreqOptions = settings.arrangement.amount_first.freq_options || [];
        const allFreqOptions = [
          { value: 3, label: 'Weekly' },
          { value: 4, label: 'Monthly' },
          { value: 6, label: this.caseService.isClient('oriel') ? 'Fortnightly' : 'Biweekly' },
        ];
        
        this.paymentFrequencyOptions = allFreqOptions.filter(option => 
          availableFreqOptions.includes(option.value.toString())
        );
      }

      if (settings?.adjust_final_payment_minimum) {
        this.adjustFinalPaymentMinimum = settings.adjust_final_payment_minimum;
      }

      if (settings?.payment_amount_limit_min) {
        this.paymentAmountLimitMin = parseFloat(settings.payment_amount_limit_min);
      }

    }

    if (this.caseData?.d_outstanding) {
      const amount = this.caseData?.d_outstanding.replace(
        this.caseData?.currency_symbol,
        ''
      );
      this.totalAmount = parseFloat(amount.replace(',', ''));
    }

    if (this.planType === 'group_plan' && Array.isArray(this.caseData)) {
      this.caseData.forEach((c) => this.caseList.push(c.id));
    }
  }

  initForm() {
    this.paymentPlanForm = this.formBuilder.group({
      payment_amount: ['', Validators.required],
      first_payment_date: ['', Validators.required],
      freq: ['', Validators.required],
    });
  }

  getOutstandingWithoutSymbol(caseData) {
    const amount = caseData?.d_outstanding.replace(
      caseData?.currency_symbol,
      ''
    );
    return parseFloat(amount.replace(',', ''));
  }

  async save() {
    if (this.paymentPlanForm.invalid) return;

    await this.commonService.showLoader('Verifying details...');

    const { payment_amount, first_payment_date, freq } =
      this.paymentPlanForm.value;

    let currency;
    let groupTotal = 0;

    if (this.planType == 'group_plan') {
      this.caseData.forEach((data) => {
        groupTotal += this.getOutstandingWithoutSymbol(data);
        currency = data.currency_symbol;
      });
      this.totalAmount = groupTotal;
    } else {
      this.totalAmount = this.getOutstandingWithoutSymbol(this.caseData);
      currency = this.caseData?.currency_symbol;
    }

    const firstPaymentDate = new Date(first_payment_date);
    const today = new Date();

    // Check for durationForFirstPayment (max days limit)
    if (this.durationForFirstPayment > 0) {
      const maxDate = new Date();
      maxDate.setDate(today.getDate() + this.durationForFirstPayment);

      if (firstPaymentDate > maxDate) {
        await this.commonService.dismissLoader();
        this.commonService.showToast(
          `First payment date should be within ${this.durationForFirstPayment} days from today.`,
          'danger'
        );
        return;
      }
    }

    const installmentAmount = parseFloat(payment_amount);
    let totalInstallments = Math.ceil(this.totalAmount / installmentAmount);
    if (this.maxTerms > 0 && this.totalAmount) {
      if (installmentAmount <= 0) {
        await this.commonService.dismissLoader();
        this.commonService.showToast(
          'Payment amount must be greater than zero.',
          'danger'
        );
        return;
      }

      // Payments per month based on frequency
      let paymentsPerMonth = 1; // Default monthly
      if (freq == 3) paymentsPerMonth = 4; // Weekly
      else if (freq == 6) paymentsPerMonth = 2; // Biweekly

      if (this.adjustFinalPaymentMinimum) {
        const lastInstallmentAmount = this.totalAmount % installmentAmount;
        if (this.paymentAmountLimitMin && lastInstallmentAmount < this.paymentAmountLimitMin) {
          totalInstallments--;
        }
      }

      // Calculate total months required for full payment
      const requiredMonths = totalInstallments / paymentsPerMonth;
      if (requiredMonths > this.maxTerms) {
        await this.commonService.dismissLoader();
        // For Oriel client, show custom message and redirect to I&E form
        let message = `Your selected plan will take approximately ${requiredMonths.toFixed(
          0
        )} months, which exceeds the maximum allowed duration of ${
          this.maxTerms
        } months.`;
        
        let buttonText = 'We need more info';
        
        if (this.caseService.isClient('oriel')) {
          message = 'We are not able to set up a plan on this basis without our team reviewing it first. Please complete the following simple questions about your income and expenditure for our team to review. We will then get back to you soon to discuss your proposal.';
          buttonText = 'Continue';
        }

        const alert = await this.alertCtrl.create({
          header: this.caseService.isClient('oriel') ? 'Additional Information Required' : 'Plan exceeds allowed duration',
          message: message,
          cssClass: 'custom-form-alert',
          buttons: [
            {
              text: this.translate.instant('COMMON.CANCEL'),
              role: 'cancel',
              handler: () => {},
            },
            {
              text: buttonText,
              handler: () => {
                this.ionNav.push(CustomFormPage, {
                  case_id: this.caseData.id,
                  debtor_id: this.caseData.debtorid,
                  form_slug: 'iande',
                  dpa_check: 'dob',
                  isFromPaymentPlan: true,
                });
              },
            },
          ],
        });
        await alert.present();
        return;
      }
    }

    await this.commonService.dismissLoader();
    this.commonService.showToast('Details verified successfully!');

    let scheduledAmount = parseFloat(payment_amount.toString()).toFixed(2);

    const options = {
      3: 'week',
      4: 'month',
      6: 'fortnight',
    };

    let text =
      totalInstallments +
      ' payments of <strong class="font-weight-bold">' +
      currency +
      scheduledAmount +
      '</strong>' +
      ' ' +
      this.paymentFrequencyLabel[freq] +
      'ly';

    this.caseService.setPaymentData({
      paymentFrequencyOption: options[freq],
      text,
      amount: payment_amount,
      scheduledAmount,
      freq,
      customPlan: 1,
      limit: totalInstallments,
      total: this.totalAmount,
      caseList: this.caseList,
      date: first_payment_date,
    });

    this.ionNav.push(PaymentGatewaysPage, {
      caseData: this.caseData,
      planType: this.planType,
      selectedPlanData: text,
    });
  }
}
